import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import { format, parseISO, startOfMonth, addMonths } from "date-fns";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useTranslation } from "react-i18next";

// Replace this with your actual service.
import PatientService from "../../services/PatientService";

interface CalltimeLogDialogProps {
  patient_id: string;
  organization_id: string;
  token: string;
  open: boolean;
  onClose: () => void;
  refreshKey?: string | number; // Optional key to force refresh when data changes
}

interface LogEntry {
  timestamp: string;
  sessions: {
    [sessionType: string]: {
      duration: number; // duration is in minutes (fractional)
      // Other fields (e.g., start_time) can be added if needed.
    };
  };
}

export const CalltimeLogDialog: React.FC<CalltimeLogDialogProps> = ({
  patient_id,
  organization_id,
  token,
  open,
  onClose,
  refreshKey,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  // State for the currently displayed month.
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  // Cache call logs data per month keyed by "yyyy-MM"
  const cacheRef = useRef<{ [monthKey: string]: LogEntry[] }>({});

  // Fetch call logs when dialog opens, selected month changes, or refresh key changes.
  useEffect(() => {
    if (open) {
      fetchCallLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedDate, refreshKey]);

  // Clear cache when refresh key changes
  useEffect(() => {
    cacheRef.current = {};
  }, [refreshKey]);

  const fetchCallLogs = async () => {
    setLoading(true);
    const monthKey = format(selectedDate, "yyyy-MM");
    if (cacheRef.current[monthKey]) {
      setLogEntries(cacheRef.current[monthKey]);
      setLoading(false);
      return;
    }
    try {
      const logMonth = format(startOfMonth(selectedDate), "yyyy-MM");
      const response = await PatientService.fetchCallLog(
        token,
        patient_id,
        organization_id,
        logMonth
      );
      // Assuming response.timelog is an object with timestamps as keys.
      const timelogData = response.timelog;
      const entries: LogEntry[] = Object.keys(timelogData).map((timestamp) => ({
        timestamp,
        sessions: timelogData[timestamp],
      }));
      // Sort entries by timestamp (most recent first)
      entries.sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      setLogEntries(entries);
      cacheRef.current[monthKey] = entries;
    } catch (error) {
      console.error("Error fetching call logs:", error);
    }
    setLoading(false);
  };

  // Enable keyboard navigation for month selection.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        setSelectedDate((prev) => addMonths(prev, -1));
      } else if (event.key === "ArrowRight") {
        const nextMonth = addMonths(selectedDate, 1);
        const now = new Date();
        if (
          nextMonth.getFullYear() > now.getFullYear() ||
          (nextMonth.getFullYear() === now.getFullYear() &&
            nextMonth.getMonth() > now.getMonth())
        ) {
          return;
        }
        setSelectedDate(nextMonth);
      }
    };
    if (open) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, selectedDate]);

  // Group log entries by day (using the ISO date portion).
  const groupedByDay = logEntries.reduce((acc: { [day: string]: LogEntry[] }, entry) => {
    const day = entry.timestamp.split("T")[0];
    if (!acc[day]) {
      acc[day] = [];
    }
    acc[day].push(entry);
    return acc;
  }, {});

  // Sort days in descending order.
  const sortedDays = Object.keys(groupedByDay).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  );

  // Build table rows.
  const tableRows: JSX.Element[] = [];
  sortedDays.forEach((day) => {
    const dayEntries = groupedByDay[day];
    dayEntries.forEach((entry, index) => {
      // Calculate start time.
      const startTime = parseISO(entry.timestamp);
      // Sum all session durations (in minutes) for this entry.
      const totalDuration = Object.values(entry.sessions).reduce(
        (sum, session) => sum + session.duration,
        0
      );
      // Calculate end time.
      const endTime = new Date(startTime.getTime() + totalDuration * 60 * 1000);
      // Format the time range as "HH:mm - HH:mm"
      const timeRange = `${format(startTime, "HH:mm")} - ${format(endTime, "HH:mm")}`;

      // Build a string for all sessions with their individual durations.
      const sessionDurations = Object.keys(entry.sessions)
        .map((sessionType) => {
          const session = entry.sessions[sessionType];
          // Convert duration (in minutes) to total seconds.
          const totalSeconds = Math.floor(session.duration * 60);
          const minutes = Math.floor(totalSeconds / 60).toString();
          const seconds = (totalSeconds % 60).toString().padStart(2, "0");
          return `${sessionType}: ${minutes}:${seconds}`;
        })
        .join("  "); // Separate multiple sessions with double spaces

      tableRows.push(
        <TableRow key={`${entry.timestamp}-${index}`}>
          {index === 0 && (
            <TableCell
              rowSpan={dayEntries.length}
              sx={{ verticalAlign: "top", fontWeight: "bold", py: 0.5 }}
            >
              {format(parseISO(day), "MMM dd, yyyy")}
            </TableCell>
          )}
          <TableCell sx={{ verticalAlign: "top", fontWeight: "bold", py: 0.5 }}>
            {timeRange}
          </TableCell>
          <TableCell sx={{ py: 1 }}>{sessionDurations}</TableCell>
        </TableRow>
      );
    });
  });

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}
      PaperProps={{
        sx: {
          mt: 12,
          maxHeight: "calc(100vh - 64px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
          <Typography variant="h6" sx={{ textAlign: "left" }}>
            {t("Call Logs")} - {patient_id}
          </Typography>
          <Box display="flex" alignItems="center">
            <IconButton onClick={() => setSelectedDate((prev) => addMonths(prev, -1))}>
              <ArrowBackIosIcon fontSize="small" />
            </IconButton>
            {/* Fixed-width container for the date display */}
            <Box sx={{ width: "220px", textAlign: "center" }}>
              <Typography variant="h6">
                {format(selectedDate, "MMMM yyyy")}
              </Typography>
            </Box>
            <IconButton
              onClick={() => {
                const nextMonth = addMonths(selectedDate, 1);
                const now = new Date();
                if (
                  nextMonth.getFullYear() > now.getFullYear() ||
                  (nextMonth.getFullYear() === now.getFullYear() &&
                    nextMonth.getMonth() > now.getMonth())
                ) {
                  return;
                }
                setSelectedDate(nextMonth);
              }}
              disabled={
                selectedDate.getFullYear() === new Date().getFullYear() &&
                selectedDate.getMonth() === new Date().getMonth()
              }
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          p: 0,
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={100}>
            <CircularProgress />
          </Box>
        ) : tableRows.length > 0 ? (
          <TableContainer
            component={Paper}
            sx={{
              flex: 1,
              maxHeight: "calc(100vh - 200px)",
              overflowY: "auto",
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: "30%", py: 0.5 }}>
                    {t("Date")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "20%", py: 0.5 }}>
                    {t("Time")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>
                    {t("Duration")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{tableRows}</TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="h6" sx={{ marginTop: 2, textAlign: "center" }}>
            {t("No Call Logs Found")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CalltimeLogDialog;
